import { autoSortStops } from "./auto_sort_stops.js"
import { routePoint } from "./shipment_run_routes.js"
import { compareSchedules, planningError, scheduleRun } from "./shipment_run_schedule.js"

const pause = () => new Promise(resolve => setTimeout(resolve, 0))
const pointKey = point => `${point.latitude},${point.longitude}`
const edgeKey = (a, b) => `${pointKey(a)}>${pointKey(b)}`

function mapDistance(a, b) {
  const rad = Math.PI / 180
  const lat = (b.latitude - a.latitude) * rad, lng = (b.longitude - a.longitude) * rad
  const h = Math.sin(lat / 2) ** 2 + Math.cos(a.latitude * rad) * Math.cos(b.latitude * rad) * Math.sin(lng / 2) ** 2
  return 12742000 * Math.asin(Math.sqrt(Math.min(1, h)))
}

// Explore orders locally, then compare a bounded set using actual road legs.
// No candidate can replace the current order with worse receiving-hour compliance.
export async function optimizeShipmentRun(stops, { depot, context, method, getRoute, signal, progress = () => {} }) {
  if (!context) throw planningError("Enter a departure time before auto sorting.")
  if (!["finish", "distance", "waiting"].includes(method)) throw planningError("Choose an auto-sort method.")
  if (!routePoint(depot)) throw planningError("Choose a starting depot before auto sorting.")
  if (stops.some(stop => !routePoint(stop.position))) throw planningError("Every stop needs a map pin before auto sorting. You can still adjust the order manually.")
  const checkpoint = async () => { signal?.throwIfAborted(); await pause(); signal?.throwIfAborted() }
  await checkpoint()
  const originalRoute = await getRoute(stops, { signal })
  const before = scheduleRun(stops, { context, legs: originalRoute.legs })
  if (before.incomplete) throw planningError("Road travel times are incomplete. Retry estimates before auto sorting.")
  if (stops.length < 2) return { keys: stops.map(stop => stop.key), before, after: before, route: originalRoute }

  const known = new Map()
  let previous = depot, mapLength = 0
  stops.forEach((stop, index) => {
    known.set(edgeKey(previous, stop.position), originalRoute.legs[index])
    mapLength += mapDistance(previous, stop.position)
    previous = stop.position
  })
  const millisPerMeter = mapLength > 0 ? before.driving * 60000 / mapLength : 3600000 / 35000
  const legFor = (from, to) => {
    if (pointKey(from) === pointKey(to)) return { distanceMeters: 0, durationMillis: 0 }
    const meters = mapDistance(from, to)
    return known.get(edgeKey(from, to)) || { distanceMeters: meters, durationMillis: meters * millisPerMeter }
  }
  const estimate = order => {
    let from = depot
    return scheduleRun(order, { context, legs: order.map(stop => { const leg = legFor(from, stop.position); from = stop.position; return leg }) })
  }
  const byKey = new Map(stops.map(stop => [stop.key, stop]))
  const spatial = (await autoSortStops(stops, { depot, signal })).keys.map(key => byKey.get(key))
  const deadline = [...stops].sort((a, b) => (context.window(a).close - context.window(b).close) || 0)
  const pending = [...stops], greedy = []
  let cursor = context.start, from = depot
  while (pending.length) {
    let choice = 0, best
    pending.forEach((stop, index) => {
      const leg = legFor(from, stop.position), window = context.window(stop)
      const arrival = cursor + leg.durationMillis / 60000
      const wait = Math.max(0, window.open - arrival), end = arrival + wait + (stop.serviceMinutes ?? 5)
      const late = Math.max(0, end - window.close)
      const urgent = window.close - end <= 30 ? 0 : 1
      const secondary = method === "distance" ? leg.distanceMeters : method === "waiting" ? wait : end - cursor
      const score = [Number(late > 0), late, urgent, secondary, window.close]
      if (!best || score.some((value, i) => value < best[i] && score.slice(0, i).every((v, j) => v === best[j]))) { best = score; choice = index }
    })
    const [next] = pending.splice(choice, 1)
    const window = context.window(next)
    cursor = Math.max(cursor + legFor(from, next.position).durationMillis / 60000, window.open) + (next.serviceMinutes ?? 5)
    from = next.position
    greedy.push(next)
    if (greedy.length % 32 === 0) await checkpoint()
  }
  const candidates = [stops, spatial, deadline, greedy].map(order => ({ order, schedule: estimate(order) }))
  candidates.sort((a, b) => compareSchedules(a.schedule, b.schedule, method))
  // Relocation handles urgent stops hidden inside an otherwise short route.
  if (stops.length <= 60) {
    let best = candidates[0], checks = 0
    for (let pass = 0; pass < 2; pass++) {
      let improved = false
      for (let i = 0; i < stops.length; i++) for (let j = 0; j < stops.length; j++) {
        if (i === j) continue
        const order = [...best.order], [stop] = order.splice(i, 1)
        order.splice(j, 0, stop)
        const schedule = estimate(order)
        if (compareSchedules(schedule, best.schedule, method) < 0) { best = { order, schedule }; improved = true }
        if (++checks % 128 === 0) await checkpoint()
      }
      if (!improved) break
    }
    candidates.push(best)
  }
  candidates.sort((a, b) => compareSchedules(a.schedule, b.schedule, method))
  const seen = new Set([JSON.stringify(stops.map(stop => stop.key))])
  const finalists = candidates.filter(candidate => {
    const key = JSON.stringify(candidate.order.map(stop => stop.key))
    if (seen.has(key)) return false
    seen.add(key)
    return true
  }).slice(0, stops.length > 100 ? 2 : 3)
  let best = { order: stops, schedule: before, route: originalRoute }
  for (let index = 0; index < finalists.length; index++) {
    await checkpoint()
    progress(index + 1, finalists.length)
    const order = finalists[index].order
    const route = await getRoute(order, { signal })
    const schedule = scheduleRun(order, { context, legs: route.legs })
    if (schedule.incomplete) throw planningError("Road travel times are incomplete. Your stop order is unchanged.")
    if (compareSchedules(schedule, best.schedule, method) < 0) best = { order, schedule, route }
  }
  return { keys: best.order.map(stop => stop.key), before, after: best.schedule, route: best.route }
}
